<template>
  <div
    class="vendor-product-item-container glass-tab-page"
    :class="{ 'vendor-product-item-container--modal': isModal }"
  >
    <BrucdcumHeader v-if="!isModal" :breadcrumb="['Vendor', 'Vendor Product Item']" />
    <form class="vendor-product-item-form" @submit.prevent>
      <div class="glass-tab-view vendor-product-item-glass-shell">
        <div class="glass-tab-view__tabs">
          <TabNavigation
            v-model="activeTab"
            :tabs="tabs"
            @update:modelValue="handleTabChange"
          />
        </div>

        <div class="glass-tab-view__content">
          <div v-if="activeTab === 'basic'" class="glass-tab-view__panel">
            <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows">
              <div class="glass-tab-panel__row vendor-product-item-form-group-row">
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-input vendor-product-item-required">
                  <LabelInputField
                    input-id="vendor-product-name"
                    label="Product Name"
                    v-model="productName"
                    placeholder="Product Name"
                    flex
                    required
                  />
                </div>
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-input">
                  <LabelInputField
                    input-id="vendor-product-weight"
                    label="Weight"
                    v-model="weight"
                    type="number"
                    placeholder="Weight"
                    flex
                  />
                </div>
              </div>

              <div class="glass-tab-panel__row vendor-product-item-form-group-row">
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-dropdown">
                  <LabelDropdown
                    label="Category"
                    select-id="vendor-product-items-group"
                    v-model="itemsGroup"
                    flex
                    wide
                    :searchable="true"
                    :options="itemsGroupDropdownOptions"
                    @change="handleItemsGroupChange"
                  />
                  <div class="vendor-product-item-input-helper-text">e.g. Consumable, Repair Parts, Fixed Asset</div>
                </div>
              </div>

              <div class="glass-tab-panel__row vendor-product-item-form-group-row vendor-product-item-form-group-row--checkboxes">
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-checkbox">
                  <LabelCheckboxField
                    input-id="vendor-product-is-subitem"
                    label="Subitem of"
                    v-model="isSubitem"
                    flex
                  />
                </div>
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-checkbox">
                  <LabelCheckboxField
                    input-id="vendor-product-taxable"
                    label="Taxable"
                    v-model="taxable"
                    flex
                  />
                </div>
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-checkbox">
                  <LabelCheckboxField
                    input-id="vendor-product-commission"
                    label="Commission item"
                    v-model="commissionItem"
                    flex
                  />
                </div>
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-checkbox">
                  <LabelCheckboxField
                    input-id="vendor-product-active"
                    label="Active"
                    v-model="active"
                    flex
                  />
                </div>
              </div>

              <div v-if="isSubitem" class="glass-tab-panel__row vendor-product-item-form-group-row">
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-dropdown vendor-product-item-required">
                  <LabelDropdown
                    label="Parent Item"
                    select-id="vendor-product-parent-item"
                    v-model="subitemOf"
                    flex
                    wide
                    required
                    :searchable="true"
                    :options="parentItemDropdownOptions"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'product'" class="glass-tab-view__panel">
            <div class="glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows">
              <div class="glass-tab-panel__row vendor-product-item-form-group-row vendor-product-item-form-group-row--checkboxes">
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-checkbox">
                  <LabelCheckboxField
                    input-id="vendor-product-sell"
                    label="I sell this product"
                    v-model="iSellThisProduct"
                    flex
                  />
                </div>
                <div class="vendor-product-item-form-group vendor-product-item-form-group--label-checkbox">
                  <LabelCheckboxField
                    input-id="vendor-product-buy"
                    label="I buy this product"
                    v-model="iBuyThisProduct"
                    flex
                    disabled
                  />
                </div>
              </div>

              <div v-if="iSellThisProduct || iBuyThisProduct" class="vendor-product-item-product-config glass-tab-panel--nested">
                <div v-if="iSellThisProduct" class="vendor-product-item-product-column">
                  <h4 class="vendor-product-item-product-header">Sell Product Configuration</h4>
                  <div class="vendor-product-item-form-group-row">
                    <div
                      class="vendor-product-item-form-group vendor-product-item-form-group--label-input"
                      :class="{ 'vendor-product-item-required': iSellThisProduct }"
                    >
                      <LabelInputField
                        input-id="vendor-product-sales-price"
                        label="Sales Price(USD)"
                        v-model="salesPrice"
                        placeholder="0.00"
                        flex
                        :required="iSellThisProduct"
                        :disabled="!iSellThisProduct"
                        :has-error="!!salesPriceError"
                        @input="validatePrices"
                      >
                        <div v-if="salesPriceError" class="vendor-product-item-error-text">{{ salesPriceError }}</div>
                      </LabelInputField>
                    </div>
                    <div
                      class="vendor-product-item-form-group vendor-product-item-form-group--label-dropdown"
                      :class="{ 'vendor-product-item-required': iSellThisProduct }"
                    >
                      <LabelDropdown
                        label="Income Account"
                        select-id="vendor-product-income-account"
                        v-model="incomeAccount"
                        flex
                        wide
                        :searchable="true"
                        :required="iSellThisProduct"
                        :disabled="!iSellThisProduct"
                        :options="incomeAccountDropdownOptions"
                        @change="handleIncomeAccountChange"
                      />
                      <button
                        v-if="incomeAccount === '__add_new_account__'"
                        type="button"
                        class="vendor-product-item-action-btn"
                        @click.prevent="navigateToNewAccount"
                      >
                        Go to Add New Account
                      </button>
                    </div>
                  </div>
                  <div class="vendor-product-item-form-group vendor-product-item-form-group--label-input">
                    <LabelInputField
                      input-id="vendor-product-standard-cost"
                      label="Standard Cost(USD)"
                      v-model="standardCost"
                      placeholder="0.00"
                      flex
                    />
                  </div>
                  <div class="vendor-product-item-form-group-row vendor-product-item-form-group-row--full">
                    <div class="vendor-product-item-form-group vendor-product-item-form-group--label-description">
                      <LabelDescriptionField
                        input-id="vendor-product-sales-description"
                        label="Description on Sales Transaction"
                        v-model="salesDescription"
                        placeholder="Description on sales transaction"
                        :rows="2"
                        :disabled="!iSellThisProduct"
                        flex
                      />
                    </div>
                  </div>
                </div>

                <div v-if="iBuyThisProduct" class="vendor-product-item-product-column">
                  <h4 class="vendor-product-item-product-header">Buy Product Configuration</h4>
                  <div class="vendor-product-item-form-group-row">
                    <div
                      class="vendor-product-item-form-group vendor-product-item-form-group--label-input"
                      :class="{ 'vendor-product-item-required': iBuyThisProduct }"
                    >
                      <LabelInputField
                        input-id="vendor-product-purchase-price"
                        label="Purchase Price(USD)"
                        v-model="purchasePrice"
                        placeholder="0.00"
                        flex
                        :required="iBuyThisProduct"
                        :disabled="!iBuyThisProduct"
                        :has-error="!!purchasePriceError"
                        @input="validatePrices"
                      >
                        <div v-if="purchasePriceError" class="vendor-product-item-error-text">{{ purchasePriceError }}</div>
                      </LabelInputField>
                    </div>
                    <div
                      class="vendor-product-item-form-group vendor-product-item-form-group--label-dropdown"
                      :class="{ 'vendor-product-item-required': iBuyThisProduct }"
                    >
                      <LabelDropdown
                        label="Purchase Account"
                        select-id="vendor-product-expense-account"
                        v-model="expenseAccount"
                        flex
                        wide
                        :searchable="true"
                        :required="iBuyThisProduct"
                        :disabled="!iBuyThisProduct"
                        :options="expenseAccountDropdownOptions"
                        @change="handleExpenseAccountChange"
                      />
                      <button
                        v-if="expenseAccount === '__add_new_account__'"
                        type="button"
                        class="vendor-product-item-action-btn"
                        @click.prevent="navigateToNewAccount"
                      >
                        Go to Add New Account
                      </button>
                    </div>
                  </div>
                  <div class="vendor-product-item-form-group-row">
                    <div class="vendor-product-item-form-group vendor-product-item-form-group--label-dropdown">
                      <LabelDropdown
                        label="Preferred Vendor"
                        select-id="vendor-product-preferred-vendor"
                        v-model="preferredVendor"
                        flex
                        wide
                        :searchable="true"
                        :disabled="!iBuyThisProduct"
                        :options="preferredVendorDropdownOptions"
                        @change="handlePreferredVendorChange"
                      />
                    </div>
                    <div class="vendor-product-item-form-group vendor-product-item-form-group--label-input">
                      <LabelInputField
                        input-id="vendor-product-vendor-no"
                        label="Vendor Product No"
                        v-model="vendorProductNo"
                        placeholder="Vendor Product No"
                        flex
                        :disabled="!iBuyThisProduct"
                      />
                    </div>
                  </div>
                  <div class="vendor-product-item-form-group-row vendor-product-item-form-group-row--full">
                    <div class="vendor-product-item-form-group vendor-product-item-form-group--label-description">
                      <LabelDescriptionField
                        input-id="vendor-product-purchase-description"
                        label="Description on Purchase Transaction"
                        v-model="purchaseDescription"
                        placeholder="Description on purchase transaction"
                        :rows="2"
                        :disabled="!iBuyThisProduct"
                        flex
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="vendor-product-item-form-actions glass-tab-view__actions">
        <template v-if="isModal && activeTab === 'product'">
          <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
          <PrimaryButton label="Save and Continue" icon="bi bi-check-lg" @click="handleModalSave" />
        </template>
        <template v-else-if="isModal">
          <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
          <PrimaryButton label="Next" icon="bi bi-arrow-right" @click="handleNext" />
        </template>
        <template v-else-if="activeTab === 'product'">
          <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
          <PrimaryButton label="Save and New" @click="handleSaveAndNew" />
          <PrimaryButton label="Save and Close" @click="handleSaveAndClose" />
        </template>
        <template v-else>
          <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
          <PrimaryButton label="Next" icon="bi bi-arrow-right" @click="handleNext" />
        </template>
      </div>
    </form>

    <AddNewItemGroup
      :visible="showAddGroupModal"
      :companyId="companyId"
      item-type="Product"
      @ok="onAddGroupOk"
      @cancel="onAddGroupCancel"
    />
    <VendorDetailsSearch v-if="showVendorSearchModal" @close="closeVendorSearchModal" />

    <ErrorModal
      :show="showErrorModal"
      :title="errorTitle"
      :message="errorMessage"
      :button-text="errorButtonText"
      @close="closeErrorModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      :title="successTitle"
      :message="successMessage"
      :button-text="successButtonText"
      @close="closeSuccessModal"
    />

    <ProfitLossModal
      :visible="showProfitLossModal"
      :profit-loss-data="profitLossData"
      :auto-close="false"
      :close-on-overlay-click="true"
      @close="closeProfitLossModal"
    />
  </div>
</template>

<script>
import '../styles/VendorProductItem.css';
import '../styles/vendor-product-item-glass.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import TabNavigation from '@/components/TabNavigation.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ProfitLossModal from '@/components/ProfitLossModal.vue';
import VendorDetailsSearch from '@/modules/vendor/views/VendorDetailsSearch.vue';
import ApiService from '@/services/api.service.js';
import CompanyService from '@/services/company.service.js';
import AddNewItemGroup from '@/modules/company/views/AddNewItemGroup.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';

export default {
  name: 'VendorProductItem',
  components: {
    BrucdcumHeader,
    TabNavigation,
    ErrorModal,
    SuccessModal,
    ProfitLossModal,
    VendorDetailsSearch,
    AddNewItemGroup,
    PrimaryButton,
    SecondaryButton,
    LabelInputField,
    LabelDropdown,
    LabelCheckboxField,
    LabelDescriptionField,
  },
  props: {
    isModal: {
      type: Boolean,
      default: false,
    },
    companyIdOverride: {
      type: [String, Number],
      default: '',
    },
    preferredVendorId: {
      type: [String, Number],
      default: '',
    },
  },
  emits: ['saved', 'close'],
  data() {
    return {
      tabs: [
        { id: 'basic', name: 'Basic Information' },
        { id: 'product', name: 'Product Information' },
      ],
      activeTab: 'basic',
      productName: '',
      weight: 0,
      isSubitem: false,
      subitemOf: '',
      iSellThisProduct: false,
      salesDescription: '',
      salesPrice: 0.0,
      incomeAccount: '',
      taxable: true,
      commissionItem: false,
      standardCost: 0.0,
      itemsGroup: '',
      active: true,
      iBuyThisProduct: true,
      purchaseDescription: '',
      purchasePrice: 0.0,
      expenseAccount: '',
      preferredVendor: '',
      vendorProductNo: '',
      showVendorSearchModal: false,
      incomeAccounts: [],
      expenseAccounts: [],
      itemCategories: [],
      showAddGroupModal: false,
      previousItemsGroup: '',
      vendorsList: [],
      salesPriceError: '',
      purchasePriceError: '',
      productItemsList: [],
      showErrorModal: false,
      errorTitle: 'Validation Error',
      errorMessage: '',
      errorButtonText: 'OK',
      showSuccessModal: false,
      successTitle: 'Success',
      successMessage: '',
      successButtonText: 'OK',
      saveAction: null,
      showProfitLossModal: false,
      profitLossData: {
        type: 'profit',
        amount: 0,
        percentage: 0,
        message: '',
      },
    };
  },
  computed: {
    companyId() {
      return this.companyIdOverride || this.$route.params.id;
    },
    itemsGroupDropdownOptions() {
      return [
        { value: '', label: 'Select category' },
        ...(this.itemCategories || []).map((group) => ({
          value: String(group.id),
          label: group.name,
        })),
        { value: '__create_new__', label: 'Add New Category' },
      ];
    },
    parentItemDropdownOptions() {
      return [
        { value: '', label: 'Select parent item' },
        ...(this.productItemsList || []).map((item) => ({
          value: String(item.id),
          label: item.productName,
        })),
      ];
    },
    incomeAccountDropdownOptions() {
      return [
        { value: '', label: 'Select account' },
        ...(this.incomeAccounts || []).map((acc) => ({
          value: String(acc.id),
          label: `${acc.accountNumber} - ${acc.accountName} - ${acc.accountType}`,
        })),
        { value: '__add_new_account__', label: 'Add New Account' },
      ];
    },
    expenseAccountDropdownOptions() {
      return [
        { value: '', label: 'Select account' },
        ...(this.expenseAccounts || []).map((acc) => ({
          value: String(acc.id),
          label: `${acc.accountNumber} - ${acc.accountName} - ${acc.accountType}`,
        })),
        { value: '__add_new_account__', label: 'Add New Account' },
      ];
    },
    preferredVendorDropdownOptions() {
      return [
        { value: '', label: 'Select vendor' },
        ...(this.vendorsList || []).map((vendor) => ({
          value: String(vendor.id),
          label: vendor.vendorName || vendor.name,
        })),
        { value: '__add_new_vendor__', label: 'Add New Vendor' },
      ];
    },
  },
  watch: {
    salesPrice: 'validatePrices',
    purchasePrice: 'validatePrices',
    isSubitem(val) {
      if (!val) {
        this.subitemOf = '';
      }
    },
  },
  async created() {
    try {
      const res = await ApiService.getCompanyAccounts(this.companyId);
      const allAccounts = res.data.accounts || res.data || [];
      this.incomeAccounts = allAccounts.filter((a) => ['Income', 'Other Income'].includes(a.accountType));
      this.expenseAccounts = allAccounts.filter((a) =>
        ['Expense', 'Other Expense', 'Cost of Goods Sold', 'Fixed Asset'].includes(a.accountType),
      );
      const itemCategories = await CompanyService.getAllItemGroups(this.companyId, 'Product');
      this.itemCategories = Array.isArray(itemCategories) ? itemCategories : [];
      const vendors = await ApiService.getAllVendors(this.companyId);
      this.vendorsList = Array.isArray(vendors) ? vendors : [];
      const productItemsRes = await ApiService.get(`/vendor/product-items/company/${this.companyId}`);
      this.productItemsList = Array.isArray(productItemsRes.data) ? productItemsRes.data : [];
      if (this.preferredVendorId) {
        this.preferredVendor = String(this.preferredVendorId);
      }
    } catch (e) {
      this.incomeAccounts = [];
      this.expenseAccounts = [];
      this.itemCategories = [];
      this.vendorsList = [];
      this.productItemsList = [];
    }
  },
  methods: {
    resolveSelectValue(eventOrValue) {
      if (eventOrValue != null && typeof eventOrValue === 'object' && 'target' in eventOrValue) {
        return eventOrValue.target.value;
      }
      return eventOrValue;
    },
    handleTabChange(newTab) {
      this.activeTab = newTab;
    },
    handleNext() {
      const validationErrors = this.validateCurrentTab();
      if (Object.keys(validationErrors).length > 0) {
        this.showValidationError(validationErrors);
        return;
      }
      const tabs = ['basic', 'product'];
      const currentIndex = tabs.indexOf(this.activeTab);
      if (currentIndex < tabs.length - 1) {
        this.activeTab = tabs[currentIndex + 1];
      }
    },
    validateCurrentTab() {
      const errors = {};
      if (this.activeTab === 'basic') {
        if (!this.productName || this.productName.trim() === '') {
          errors.productName = 'Product Name is required.';
        }
        if (this.isSubitem && (!this.subitemOf || this.subitemOf === '')) {
          errors.subitemOf = 'Parent Item is required when Subitem is selected.';
        }
      } else if (this.activeTab === 'product') {
        if (!this.iSellThisProduct && !this.iBuyThisProduct) {
          errors.productType = 'Please select at least one product type (I sell this product or I buy this product).';
        }
        if (this.iSellThisProduct) {
          if (!this.salesPrice || this.salesPrice === '' || this.salesPrice === 0) {
            errors.salesPrice = 'Sales Price is required when selling this product.';
          }
          if (!this.incomeAccount || this.incomeAccount === '' || this.incomeAccount === '__add_new_account__') {
            errors.incomeAccount = 'Income Account is required when selling this product.';
          }
        }
        if (this.iBuyThisProduct) {
          if (!this.purchasePrice || this.purchasePrice === '' || this.purchasePrice === 0) {
            errors.purchasePrice = 'Purchase Price is required when buying this product.';
          }
          if (!this.expenseAccount || this.expenseAccount === '' || this.expenseAccount === '__add_new_account__') {
            errors.expenseAccount = 'Purchase Account is required when buying this product.';
          }
        }
        if (this.salesPriceError) {
          errors.salesPrice = this.salesPriceError;
        }
        if (this.purchasePriceError) {
          errors.purchasePrice = this.purchasePriceError;
        }
      }
      return errors;
    },
    showValidationError(validationErrors) {
      const errorMessages = Object.values(validationErrors)
        .map((message) => `• ${message}`)
        .join('\n');
      this.errorTitle = 'Please Complete Required Fields';
      this.errorMessage = `Please fix the following issues before continuing:\n\n${errorMessages}`;
      this.errorButtonText = 'OK';
      this.showErrorModal = true;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successMessage = '';
      if (this.isModal) {
        this.saveAction = null;
        return;
      }
      if (this.saveAction === 'saveAndClose') {
        this.$router.push({ name: 'VendorItemsList', params: { id: this.companyId } });
      } else if (this.saveAction === 'saveAndNew') {
        this.resetForm();
      }
      this.saveAction = null;
    },
    closeProfitLossModal() {
      this.showProfitLossModal = false;
    },
    handleSaveAndNew() {
      const allErrors = this.validateAllTabs();
      if (Object.keys(allErrors).length > 0) {
        this.showValidationError(allErrors);
        return;
      }
      this.saveAction = 'saveAndNew';
      this.saveVendorProductItem();
    },
    handleSaveAndClose() {
      const allErrors = this.validateAllTabs();
      if (Object.keys(allErrors).length > 0) {
        this.showValidationError(allErrors);
        return;
      }
      this.saveAction = 'saveAndClose';
      this.saveVendorProductItem();
    },
    handleModalSave() {
      const allErrors = this.validateAllTabs();
      if (Object.keys(allErrors).length > 0) {
        this.showValidationError(allErrors);
        return;
      }
      this.saveAction = 'modalSave';
      this.saveVendorProductItem();
    },
    validateAllTabs() {
      const errors = {};
      if (!this.productName || this.productName.trim() === '') {
        errors.productName = 'Product Name is required.';
      }
      if (this.isSubitem && (!this.subitemOf || this.subitemOf === '')) {
        errors.subitemOf = 'Parent Item is required when Subitem is selected.';
      }
      if (!this.iSellThisProduct && !this.iBuyThisProduct) {
        errors.productType = 'Please select at least one product type (I sell this product or I buy this product).';
      }
      if (this.iSellThisProduct) {
        if (!this.salesPrice || this.salesPrice === '' || this.salesPrice === 0) {
          errors.salesPrice = 'Sales Price is required when selling this product.';
        }
        if (!this.incomeAccount || this.incomeAccount === '' || this.incomeAccount === '__add_new_account__') {
          errors.incomeAccount = 'Income Account is required when selling this product.';
        }
      }
      if (this.iBuyThisProduct) {
        if (!this.purchasePrice || this.purchasePrice === '' || this.purchasePrice === 0) {
          errors.purchasePrice = 'Purchase Price is required when buying this product.';
        }
        if (!this.expenseAccount || this.expenseAccount === '' || this.expenseAccount === '__add_new_account__') {
          errors.expenseAccount = 'Purchase Account is required when buying this product.';
        }
      }
      if (this.salesPriceError) {
        errors.salesPrice = this.salesPriceError;
      }
      if (this.purchasePriceError) {
        errors.purchasePrice = this.purchasePriceError;
      }
      return errors;
    },
    closeVendorSearchModal() {
      this.showVendorSearchModal = false;
    },
    handleIncomeAccountChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new_account__') {
        this.navigateToNewAccount();
        this.incomeAccount = '';
      } else {
        this.incomeAccount = selected;
      }
    },
    handleExpenseAccountChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new_account__') {
        this.navigateToNewAccount();
        this.expenseAccount = '';
      } else {
        this.expenseAccount = selected;
      }
    },
    handleItemsGroupChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__create_new__') {
        this.showAddGroupModal = true;
        this.itemsGroup = this.previousItemsGroup || '';
      } else {
        this.itemsGroup = selected;
        this.previousItemsGroup = selected;
      }
    },
    onAddGroupCancel() {
      this.showAddGroupModal = false;
      this.itemsGroup = this.previousItemsGroup || '';
    },
    async onAddGroupOk(group) {
      this.showAddGroupModal = false;
      if (group && group.id) {
        if (!this.itemCategories.some((g) => g.id === group.id)) {
          this.itemCategories.push(group);
        }
        this.itemsGroup = String(group.id);
        this.previousItemsGroup = String(group.id);
      }
    },
    handlePreferredVendorChange(eventOrValue) {
      const selected = this.resolveSelectValue(eventOrValue);
      if (selected === '__add_new_vendor__') {
        this.$router.push({ name: 'AddNewVendor', params: { id: this.companyId } });
        this.preferredVendor = '';
      } else {
        this.preferredVendor = selected;
      }
    },
    async saveVendorProductItem() {
      if (this.salesPriceError || this.purchasePriceError) {
        this.errorTitle = 'Validation Error';
        this.errorMessage = 'Please fix the errors before saving.';
        this.showErrorModal = true;
        return;
      }
      try {
        const payload = {
          productName: this.productName,
          weight: this.weight,
          isSubitem: this.isSubitem,
          subitemOf: this.isSubitem ? this.subitemOf : null,
          iSellThisProduct: this.iSellThisProduct,
          salesDescription: this.salesDescription,
          salesPrice: this.salesPrice,
          incomeAccountId: this.incomeAccount || null,
          taxable: this.taxable,
          commissionItem: this.commissionItem,
          standardCost: this.standardCost,
          itemGroupId: this.itemsGroup || null,
          active: this.active,
          iBuyThisProduct: this.iBuyThisProduct,
          purchaseDescription: this.purchaseDescription,
          purchasePrice: this.purchasePrice,
          expenseAccountId: this.expenseAccount || null,
          preferredVendor: this.preferredVendor || null,
          vendorProductNo: this.vendorProductNo,
          companyId: this.companyId,
        };
        const res = await ApiService.post('/vendor/product-items', payload);
        const item = res.data;

        if (this.isModal && this.saveAction === 'modalSave') {
          this.$emit('saved', this.mapSavedProductItem(item));
          this.saveAction = null;
          return;
        }

        this.successTitle = 'Vendor Product Item Created Successfully';
        this.successMessage = `The vendor product item "${this.productName}" has been created successfully.`;
        this.successButtonText = 'OK';
        this.showSuccessModal = true;
      } catch (error) {
        this.errorTitle = 'Error';
        this.errorMessage =
          'Error saving Vendor Product Item: ' + (error.response?.data?.error || error.message);
        this.showErrorModal = true;
      }
    },
    calculateProfitLoss() {
      const salesPrice = Number(this.salesPrice) || 0;
      const purchasePrice = Number(this.purchasePrice) || 0;

      if (
        this.iSellThisProduct &&
        this.iBuyThisProduct &&
        salesPrice > 0 &&
        purchasePrice > 0 &&
        !this.salesPriceError &&
        !this.purchasePriceError &&
        !this.isModal
      ) {
        const profitAmount = salesPrice - purchasePrice;
        const profitPercentage = purchasePrice > 0 ? (profitAmount / purchasePrice) * 100 : 0;
        let type;
        let message;

        if (profitAmount > 0) {
          type = 'profit';
          message = `You will make a profit of $${profitAmount.toFixed(2)} on this product`;
        } else if (profitAmount < 0) {
          type = 'loss';
          message = `You will incur a loss of $${Math.abs(profitAmount).toFixed(2)} on this product`;
        } else {
          type = 'break-even';
          message = 'This product will break even (no profit or loss)';
        }

        this.profitLossData = {
          type,
          amount: Math.abs(profitAmount).toFixed(2),
          percentage: Math.abs(profitPercentage).toFixed(1),
          message,
        };
        this.showProfitLossModal = true;
      }
    },
    mapSavedProductItem(item) {
      return {
        id: item.id,
        name: item.productName,
        type: item.type || 'Product',
        salesPrice: item.purchasePrice ?? item.salesPrice ?? 0,
        purchasePrice: item.purchasePrice,
        preferredVendor: item.preferredVendor,
      };
    },
    validatePrices() {
      if (this.iSellThisProduct) {
        if (this.salesPrice !== '' && (isNaN(this.salesPrice) || Number(this.salesPrice) < 0)) {
          this.salesPriceError = 'Sales Price (USD) must be a non-negative number.';
        } else if (
          this.iBuyThisProduct &&
          this.purchasePrice !== '' &&
          Number(this.salesPrice) < Number(this.purchasePrice)
        ) {
          this.salesPriceError = 'Sales Price (USD) should not be less than Purchase Price (USD).';
        } else {
          this.salesPriceError = '';
        }
      } else {
        this.salesPriceError = '';
      }

      if (this.iBuyThisProduct) {
        if (this.purchasePrice !== '' && (isNaN(this.purchasePrice) || Number(this.purchasePrice) < 0)) {
          this.purchasePriceError = 'Purchase Price (USD) must be a non-negative number.';
        } else {
          this.purchasePriceError = '';
        }
      } else {
        this.purchasePriceError = '';
      }

      this.calculateProfitLoss();
    },
    resetForm() {
      this.activeTab = 'basic';
      this.productName = '';
      this.weight = 0;
      this.isSubitem = false;
      this.subitemOf = '';
      this.iSellThisProduct = false;
      this.salesDescription = '';
      this.salesPrice = 0.0;
      this.incomeAccount = '';
      this.taxable = true;
      this.commissionItem = false;
      this.standardCost = 0.0;
      this.itemsGroup = '';
      this.previousItemsGroup = '';
      this.active = true;
      this.iBuyThisProduct = true;
      this.purchaseDescription = '';
      this.purchasePrice = 0.0;
      this.expenseAccount = '';
      this.preferredVendor = '';
      this.vendorProductNo = '';
      this.salesPriceError = '';
      this.purchasePriceError = '';
    },
    navigateToNewAccount() {
      this.$router.push(`/company/${this.companyId}/dashboard/new-account`);
    },
    cancel() {
      if (this.isModal) {
        this.$emit('close');
        return;
      }
      this.$router.back();
    },
  },
};
</script>
